import { RasterDatasetMetadata } from '@/stores/dataset/dataset-model';
import store from '@/stores/dataset/raster-dataset-store';
import {
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import { Bounds, LatLng } from 'leaflet';
import proj4 from 'proj4';
import { worldGeodeticProjection } from '@/plugins/proj4';

@Module({
  dynamic: true,
  namespaced: true,
  name: 'RasterDatasetModule',
  store,
})
class RasterDatasetModule extends VuexModule {
  private _metadata: RasterDatasetMetadata | null = null;
  private _isLoading = false;

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get hasLoaded(): boolean {
    return this._metadata !== null;
  }

  public get metadata(): RasterDatasetMetadata | null {
    return this._metadata;
  }

  public get isLatInsideBounds(): (lat: number) => boolean {
    return (lat: number) => {
      if (!this.metadata) {
        return false;
      }
      return (
        this.metadata.bboxOuter.south <= lat &&
        lat <= this.metadata.bboxOuter.north
      );
    };
  }

  public get isLonInsideBounds(): (lon: number) => boolean {
    return (lon: number) => {
      if (!this.metadata) {
        return false;
      }
      return (
        this.metadata.bboxOuter.west <= lon &&
        lon <= this.metadata.bboxOuter.east
      );
    };
  }

  public get isCoordInsideBounds(): (latlon: LatLng) => boolean {
    // transform latlon to projection of dataset and check if it is inside bounds
    return (latlon: LatLng) => {
      if (!this.metadata) {
        return false;
      }
      const bounds = this.metadata.gridBounds;
      const step = this.metadata.spatialResolution;
      const crs = this.metadata.crs;
      const leafletBounds = new Bounds(
        [bounds.north + step / 2, bounds.west - step / 2],
        [bounds.south - step / 2, bounds.east + step / 2]
      );
      const point = proj4(worldGeodeticProjection, crs, [
        latlon.lng,
        latlon.lat,
      ]);
      return leafletBounds.contains([point[1], point[0]]);
    };
  }

  @Mutation
  updateLoading(isLoading: boolean): void {
    this._isLoading = isLoading;
  }

  @Mutation
  updateMetadata(metadata: RasterDatasetMetadata): void {
    this._metadata = metadata;
  }
}

export default getModule(RasterDatasetModule);
